/**
 * BrandedLineLoader
 *
 * Silent loader: thin indeterminate progress bar with the workspace's wide
 * logo overlaid above. Falls back to the holito mark when no workspace logo
 * is known so the loading state is always brand-aware.
 *
 * No caption text. holito UI is visual-first: the motion of the bar carries
 * the "still working" affordance - words would be noise.
 *
 * **Bar position is fixed at the parent's centre** (the logo is absolutely
 * positioned above the bar, not in flow). This is critical because the inline
 * boot loader in `index.html` also centres a bar; matching positions makes
 * the boot → React handoff look like one continuous loader instead of two
 * different loaders snapping to different y-offsets.
 *
 * **Cached-image fast path**: when a remount happens (e.g. join → interview
 * navigation) and the workspace logo is already in the browser cache, we
 * mark it loaded synchronously via `useLayoutEffect` so the opacity fade
 * never runs. The user sees the same logo, instantly, at the same position.
 *
 * @module components/BrandedLineLoader
 */

import React, { useLayoutEffect, useRef, useState } from 'react';
import { captureException } from '../lib/telemetry/posthog/frontend';

/** Props for {@link BrandedLineLoader}. */
export interface BrandedLineLoaderProps {
  /**
   * Resolved workspace wide-logo URL. Tri-state semantics:
   *
   * - `undefined` - workspace identity not resolved yet. Render the bar
   *   alone; do not flash the holito mark while we wait.
   * - `null` - confirmed there is no workspace logo for this room. Render
   *   the holito mark as the brand fallback.
   * - `string` - render the workspace logo above the bar.
   */
  logoUrl?: string | null;
  /**
   * Accessible label applied to the workspace logo IMG. Ignored on the
   * holito fallback (which always uses "holito"). Defaults to "Loading".
   */
  alt?: string;
}

const BrandedLineLoader: React.FC<BrandedLineLoaderProps> = ({
  logoUrl,
  alt = 'Loading',
}) => {
  const trimmed = typeof logoUrl === 'string' ? logoUrl.trim() : logoUrl;
  const [failedLogoUrl, setFailedLogoUrl] = useState<string | null>(null);
  const logoState: 'unknown' | 'workspace' | 'fallback' =
    trimmed === undefined
      ? 'unknown'
      : trimmed && typeof trimmed === 'string' && trimmed !== failedLogoUrl
        ? 'workspace'
        : 'fallback';

  const imgRef = useRef<HTMLImageElement | null>(null);
  const [workspaceLogoLoaded, setWorkspaceLogoLoaded] = useState(false);

  // Phase-sync the bar animation to the boot loader's epoch (set by
  // index.html). Every BrandedLineLoader instance that mounts later picks
  // up the bar at the same sweep phase the user has been watching since
  // page load - so handoff (boot → React, /join → /interview, etc.) is
  // visually continuous instead of restarting frame 0.
  const [animationDelay] = useState(() => {
    if (typeof window === 'undefined' || typeof performance === 'undefined') {
      return '0ms';
    }
    const epoch =
      (window as Window & { __bootEpochMs?: number }).__bootEpochMs ?? 0;
    const elapsed = performance.now() - epoch;
    return `-${elapsed % 1300}ms`;
  });

  // Cache fast-path: if the image is already in the browser cache when we
  // mount (typical for the join → interview navigation, where the logo was
  // just loaded on the previous page), promote to loaded synchronously
  // before paint so no opacity fade runs.
  useLayoutEffect(() => {
    const el = imgRef.current;
    // Reset and inspect the replacement in one pre-paint step. A later
    // passive reset would hide a cached image whose load event already fired.
    setWorkspaceLogoLoaded(logoState === 'workspace' && !!el?.complete && el.naturalWidth > 0);
  }, [trimmed, logoState]);

  return (
    <>
      <style>{`
        @keyframes holito-bll-bar-sweep {
          0%   { transform: translateX(0)    scaleX(0); }
          10%  { transform: translateX(0)    scaleX(0.2); }
          40%  { transform: translateX(0)    scaleX(0.7); }
          60%  { transform: translateX(60%)  scaleX(0.4); }
          100% { transform: translateX(100%) scaleX(0.2); }
        }
        .holito-bll-fill {
          transform-origin: left center;
          animation: holito-bll-bar-sweep 1.3s linear infinite;
          will-change: transform;
        }
        @media (prefers-reduced-motion: reduce) {
          .holito-bll-fill {
            animation: none;
            transform: scaleX(0.6);
          }
        }
      `}</style>
      {/*
        Hidden 1px Inter trigger. Forces the browser to start downloading
        every weight we use on the next page so that `document.fonts.ready`
        (used by the asset-ready gate in RoomJoinPage / App) actually waits
        for the brand font instead of resolving instantly. Aria-hidden +
        zero size so it's invisible and inert.
      */}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: 'none',
          overflow: 'hidden',
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <span style={{ fontWeight: 400 }}>a</span>
        <span style={{ fontWeight: 500 }}>a</span>
        <span style={{ fontWeight: 600 }}>a</span>
        <span style={{ fontWeight: 700 }}>a</span>
      </span>
      {/*
        Container is the bar itself. Logo overlays absolutely above with
        `bottom-full mb-4`, so the bar's own centre line is the reference
        point. Same centre line as the boot loader in index.html.
      */}
      <div className="holito-bll-root relative h-1 w-40 rounded-full bg-border overflow-visible">
        {(logoState === 'workspace' || logoState === 'fallback') && (
          <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-4 flex items-center justify-center h-10 pointer-events-none">
            {logoState === 'workspace' && (
              <img
                ref={imgRef}
                key={`workspace:${trimmed}`}
                src={trimmed as string}
                alt={alt}
                onLoad={() => setWorkspaceLogoLoaded(true)}
                onError={() => {
                  setFailedLogoUrl(trimmed as string);
                  captureException(new Error('Workspace logo could not load'), 'BrandedLineLoader.logo');
                }}
                className={`max-h-10 max-w-[160px] w-auto h-auto object-contain transition-opacity duration-300 ${
                  workspaceLogoLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                draggable={false}
              />
            )}
            {logoState === 'fallback' && (
              <img
                key="fallback"
                src="/holito-logo-black.svg"
                alt="holito"
                className="h-7 w-auto dark:invert"
                draggable={false}
              />
            )}
          </div>
        )}

        <div
          role="progressbar"
          aria-busy="true"
          aria-label="Loading"
          className="absolute inset-0 rounded-full overflow-hidden"
        >
          <div
            data-bar-fill
            className="holito-bll-fill absolute inset-0 rounded-full bg-volt"
            style={{ animationDelay }}
          />
        </div>
      </div>
    </>
  );
};

export default BrandedLineLoader;
